import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Helmet from 'react-helmet';
import { styled } from '@mui/material/styles';

import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  GlobalStyles,
  Grid,
  TextField,
  Typography
} from '@mui/material';
import '../../App.css';

import axios from 'axios';
import { getApiBaseUrl } from '../../utils/env';
import PublicLayout from './PublicLayout';
import PropTypes from 'prop-types';
import {
  ContentWrap,
  GridCenterAligned as GridCenterAlignedBase,
  GridContentItem
} from '../common/GridLayout';
import { Headline as BaseHeadline } from '../common/PublicTypography';
import { shadeBgStyles } from '../../utils/styles';
import { ReactComponent as ArrowIcon } from '../../icons/button-arrow-white.svg';

const GridMobileWithBackground = styled(Grid)`
    position: relative;

    &:before {
        ${props => props.theme.breakpoints.down('md')} {
            content: '';
            background-image: url('/bg-home.png');
            height: 100%;
            position: absolute;
            top: 0;
            width: calc(100vw + 30px);
            left: -30px;
            transform: scaleY(-1);
            background-size: 100% 88%;
            background-repeat: no-repeat;
        }
    }
`;

const Headline = styled(BaseHeadline)`
    font-size: 16px;
    line-height: 1.14;
    max-width: 280px;
    margin-top: 0;

    ${props => props.theme.breakpoints.up('md')} {
        font-size: 32px;
        max-width: none;
    }
`;

const LoginShowcaseImage = styled('div')`
    width: 100%;
    aspect-ratio: 1 / 0.75;
    position: relative;
    overflow: hidden;

    ${props => props.theme.breakpoints.up('md')} {
        overflow: visible;
        flex: 1;
    }

    &:after {
        content: '';
        background-image: linear-gradient(to bottom, rgb(235, 241, 255, 0), rgb(235, 241, 255, 0.4)),
        url(/mission-control-final-2.svg);
        height: 100%;
        position: absolute;
        top: 10px;
        width: 100%;
        background-size: 100%;
        background-position: top left;
        background-repeat: no-repeat no-repeat;
        z-index: 0;

        ${props => props.theme.breakpoints.up('md')} {
            background-image: url(/mission-control-final-2.svg);
            top: 20px;
            height: calc(100% + 130px);
            background-size: contain;
            width: 200%;
            background-position: top left 10px;
            background-repeat: no-repeat;
        }
    }
`;

const Form = styled('form')`
    background-color: #ebf1ee;
    padding: 30px;
    border-radius: 4px;
    margin-bottom: 20px;

    ${props => props.theme.breakpoints.up('md')} {
        margin-top: 20px;
        margin-right: 40px;
    }
`;

const ErrorMessage = styled(Typography)`
    color: #d32f2f;
    margin: 16px 0;
`;

const SuccessMessage = styled(Typography)`
    color: darkgreen;
    margin: 16px 0;
`;

const FormCtaRoot = styled(Box)`
    position: relative;
    padding-top: 30px;
`;

const GridCenterAligned = styled(GridCenterAlignedBase)`
    ${props => props.theme.breakpoints.up('md')} {
        gap: 70px;
    }
`;

function ForgotPassword({ className }) {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [serverError, setServerError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validate = () => {
    if (!EMAIL_REGEX.test(email)) {
      setError('Invalid email address');
      return false;
    }
    return true;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (busy) return;

    if (validate()) {
      setBusy(true);
      setServerError('');
      setSuccessMessage('');

      await axios.post(`${getApiBaseUrl()}/auth/reset-password`, {
        email,
      }).then(response => {
        setBusy(false);
        setSuccessMessage('Please check your email for password reset link. If you do not' +
          ' receive it within 10 minutes, please check your spam folder. If you still do not' +
          ' receive it, please contact support.');
        setEmail('');
        setError('');
      }).catch(error => {
        setBusy(false);
        setServerError(typeof error === 'string' ? error : 'Sorry, something went wrong. Please try again later.');
      });
    }
  };

  useEffect(() => {
    setEmail(searchParams.get('email') || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const pageBackgroundStyles = {
    body: {
      backgroundColor: '#274bde',
      backgroundImage: 'url("/v2/pixels-bg.svg")',
      backgroundPosition: 'top left',
      backgroundRepeat: 'repeat-x',
    },
  };

  return (
    <PublicLayout className={className}>
      <GlobalStyles styles={{ ...shadeBgStyles, ...pageBackgroundStyles }}/>
      <Helmet title="Pixels - Forgot password">
        <meta
          name="description"
          content="Welcome to Pixels! Forgot password? Request a new password here."
        />
      </Helmet>
      <GridCenterAligned container>
        <GridContentItem item xs={12} md={6}>
          <ContentWrap className={'ctn-root'} id="ContentWrap">
            <Headline variant={'h1'}>Forgot password</Headline>
            <Typography variant={'body1'}>We will send you reset password link if your email is registered in our system.</Typography>

            <Form onSubmit={handleSubmit}>
              <Grid container rowSpacing={2} spacing={3}>
                {serverError && (<Grid item xs={12}>
                  <ErrorMessage>{serverError}</ErrorMessage>
                </Grid>)}
                {successMessage && (<Grid item xs={12}><SuccessMessage>{successMessage}</SuccessMessage></Grid>)}
                <Grid item md={12} xs={12}>
                  <FormControl margin="dense" fullWidth>
                    <TextField
                      label="Email"
                      type={'email'}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      error={!!error}
                      helperText={error}
                      required
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <FormCtaRoot>
                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                  <Grid item xs={12}>
                    <Button
                      type={'submit'}
                      style={{
                        fontSize: 17,
                        fontWeight: 700,
                        minHeight: '50px',
                        backgroundColor: '#3355D1',
                        textTransform: 'none',
                        justifyContent: 'space-between',
                      }}
                      fullWidth
                      variant="contained"
                      startIcon={<></>}
                      endIcon={busy ? <CircularProgress size={24} color="inherit"/> : <ArrowIcon/>}
                    >
                      Reset password
                    </Button>
                  </Grid>
                </Grid>
              </FormCtaRoot>
            </Form>
          </ContentWrap>
        </GridContentItem>
        <Grid
          item
          xs={12}
          md={6}
          sx={theme => ({
            display: 'flex',
            flexDirection: 'column',
            [theme.breakpoints.down('md')]: {
              display: 'none',
            },
          })}
          id="LoginShowcaseImageWrapper"
        >
        </Grid>
      </GridCenterAligned>
      <GridMobileWithBackground
        sx={theme => ({
          [theme.breakpoints.up('md')]: {
            display: 'none',
          },
        })}
      >
        <LoginShowcaseImage/>
      </GridMobileWithBackground>
    </PublicLayout>
  );
}

ForgotPassword.propTypes = {
  className: PropTypes.string,
};

export default styled(ForgotPassword)`
    .content-root {
        display: flex;
    }

    .content-inner {
        max-width: none;
        padding: 0;
        overflow: hidden;
        display: flex;
        flex-direction: column;

        > div:first-of-type {
            flex: 1;
        }
    }

    ${props => props.theme.breakpoints.up('md')} {
        .ctn-root {
            padding-top: 20px;
        }
    }
`;
